import { format, isSameDay, type Locale } from "date-fns"
import { useRef } from "react"
import { getMonthDaysByWeeks, getWeekDays } from "../utils"
import { CalendarDayPickerDay } from "./CalendarDayPickerDay"

type CalendarDayPickerMonthPropsType = {
  month: Date
  selectedDate?: Date | null
  onSelectDate: (date: Date) => void
  onChangeDate: (date: Date, disabled: boolean) => void
  onLeaveDate?: (date: Date) => void
  firstAvailableDate: Date
  lastAvailableDate?: Date
  locale?: Locale
}

function isDisabled(
  day: Date,
  firstAvailableDate: Date,
  lastAvailableDate: Date | undefined,
) {
  return (
    day.getTime() < firstAvailableDate.getTime() ||
    (lastAvailableDate != null && day.getTime() > lastAvailableDate.getTime())
  )
}

function areEqualDays(
  date1: Date | null | undefined,
  date2: Date | null | undefined,
) {
  return date1 != null && date2 != null && isSameDay(date1, date2)
}

const weekdays = getWeekDays()
const today = new Date().setHours(0, 0, 0, 0)

export function CalendarDayPickerMonth({
  month,
  selectedDate,
  onSelectDate,
  onChangeDate,
  onLeaveDate,
  firstAvailableDate,
  lastAvailableDate,
  locale,
}: CalendarDayPickerMonthPropsType) {
  const byWeeks = useRef(getMonthDaysByWeeks(month))

  const onMouseEnter = (day: Date, disabled: boolean) => {
    onChangeDate(day, disabled)
  }

  const onSelect = (targetDate: Date) => {
    if (!areEqualDays(targetDate, selectedDate)) {
      onSelectDate(targetDate)
    }
  }

  return (
    <div
      className="cobalt-CalendarDayPicker__month"
      data-month={format(month, "yyyy-MM")}
    >
      <div className="cobalt-CalendarDayPicker__month-header">
        {format(month, "MMMM yyyy", { locale })}
      </div>
      <div className="cobalt-CalendarDayPicker__month__weeks-container">
        <div className="cobalt-CalendarDayPicker__month__week-header">
          {weekdays.map((weekday) => (
            <div
              key={weekday.getTime()}
              className="cobalt-CalendarDayPicker__month__day-header"
            >
              {format(weekday, "iiiiii", { locale })}
            </div>
          ))}
        </div>
        {byWeeks.current.map((week, index) => (
          <div
            key={`week-${index}-${week.map((day) => day.getTime()).join("-")}`}
            className="cobalt-CalendarDayPicker__month__week"
          >
            {week.map((day) => {
              return (
                <CalendarDayPickerDay
                  key={day.getTime()}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onLeaveDate}
                  date={day}
                  onSelect={onSelect}
                  isToday={isSameDay(day, today)}
                  disabled={isDisabled(
                    day,
                    firstAvailableDate,
                    lastAvailableDate,
                  )}
                  selected={areEqualDays(day, selectedDate)}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
